import {
  FINANCEIRO_OBRAS_TABLE_COLUMNS_KEY,
  FINANCEIRO_OBRAS_TABLE_DENSITY_KEY,
} from "@/core/constants/keys";
import type { FilterValue } from "@/ds";
import type { FinanceiroObrasFilters } from "@/modules/financeiro-obras";
import {
  FINANCEIRO_NO_VALUE,
  FINANCEIRO_TABLE_SETTINGS_FORMAT_KEY,
  PERIOD_COLUMN,
} from "./financeiro-obras.constants";

/** Conjunto completo de filtros no formato dos filtros salvos (`Record<string, string[]>`). */
export function toSavedValue(filters: FinanceiroObrasFilters): FilterValue {
  return {
    statusFinanceiro: filters.statusFinanceiro,
    embarcacaoId: filters.embarcacaoId,
    tipoObra: filters.tipoObra,
    condicao: filters.condicao,
    dataInicio: filters.dataInicio ? [filters.dataInicio] : [],
    dataFim: filters.dataFim ? [filters.dataFim] : [],
  };
}

/** Valor da coluna Data do painel → início e fim: data fixa (`[dia]`) vale como início = fim; período só completo. */
export function periodoToDatas(periodo: string[]): [string, string] {
  if (periodo.length === 1) return [periodo[0] ?? "", periodo[0] ?? ""];
  return periodo.length === 2 && periodo[0] && periodo[1] ? [periodo[0], periodo[1]] : ["", ""];
}

/** Critérios no formato do painel de filtros: o período entra como `[início, fim]`, e início = fim como data fixa. */
export function toPanelValue(filters: FinanceiroObrasFilters): FilterValue {
  return {
    ...toSavedValue(filters),
    [PERIOD_COLUMN]:
      filters.dataInicio && filters.dataFim
        ? filters.dataInicio === filters.dataFim
          ? [filters.dataInicio]
          : [filters.dataInicio, filters.dataFim]
        : [],
  };
}

export function fromSavedValue(value: FilterValue): FinanceiroObrasFilters {
  return {
    statusFinanceiro: value.statusFinanceiro ?? [],
    embarcacaoId: value.embarcacaoId ?? [],
    tipoObra: value.tipoObra ?? [],
    condicao: value.condicao ?? [],
    dataInicio: value.dataInicio?.[0] ?? "",
    dataFim: value.dataFim?.[0] ?? "",
  };
}

/** `yyyy-MM-dd` → `dd/MM/aa` (protótipo). */
export function formatObraDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return FINANCEIRO_NO_VALUE;
  return `${match[3]}/${match[2]}/${(match[1] ?? "").slice(2)}`;
}

/** `yyyy-MM-dd` → `dd/MM/aaaa` (resumo de filtros do arquivo exportado). */
export function formatObraDateLong(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

export function formatDuration(days: number | null): string {
  return days === null ? FINANCEIRO_NO_VALUE : String(days);
}

/** Realizado/Outlook BRL M: já em milhões de reais, 1 casa decimal (mesmo formato do módulo Obras); ausente = "-". */
export function formatMillions(value: number | null): string {
  return value === null
    ? FINANCEIRO_NO_VALUE
    : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
        value,
      );
}

/** Percentual (RE, EM, CO, ES, NC): até 1 casa decimal e "%" (mesmo formato do módulo Obras); ausente = "-". */
export function formatPercent(value: number | null): string {
  return value === null
    ? FINANCEIRO_NO_VALUE
    : `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(value)}%`;
}

/** Resumo do período filtrado para o arquivo exportado; vazio sem período. */
export function formatPeriodo(filters: Pick<FinanceiroObrasFilters, "dataInicio" | "dataFim">) {
  if (!filters.dataInicio && !filters.dataFim) return undefined;

  const start = filters.dataInicio ? formatObraDateLong(filters.dataInicio) : "…";
  const end = filters.dataFim ? formatObraDateLong(filters.dataFim) : "…";
  return `${start} a ${end}`;
}

/**
 * Opcionais do formato antigo (só Duração Total existia): a lista salva de visíveis é convertida só nelas,
 * então as colunas financeiras novas continuam visíveis para quem já tinha preferências salvas.
 */
const LEGACY_OPTIONAL_COLUMNS = ["duracao"];

/** Versão do formato das preferências da tabela no armazenamento. */
const SETTINGS_FORMAT_VERSION = "ds-v1";

const LEGACY_DENSITY: Record<string, "normal" | "small"> = {
  confortavel: "normal",
  compacto: "small",
};

/** Densidade antiga (crua ou entre aspas) para a do DS; qualquer outro valor fica como está. */
function migrateDensity(storage: Storage) {
  const raw = storage.getItem(FINANCEIRO_OBRAS_TABLE_DENSITY_KEY);
  const migrated = raw === null ? undefined : LEGACY_DENSITY[raw.replace(/^"|"$/g, "")];
  if (migrated) storage.setItem(FINANCEIRO_OBRAS_TABLE_DENSITY_KEY, JSON.stringify(migrated));
}

/** Colunas opcionais visíveis (formato antigo) para a lista de ocultas; valor inválido volta ao padrão. */
function migrateColumns(storage: Storage) {
  if (storage.getItem(FINANCEIRO_TABLE_SETTINGS_FORMAT_KEY) === SETTINGS_FORMAT_VERSION) return;

  const raw = storage.getItem(FINANCEIRO_OBRAS_TABLE_COLUMNS_KEY);
  if (raw !== null) {
    try {
      const visible: unknown = JSON.parse(raw);
      if (
        Array.isArray(visible) &&
        visible.every((key) => LEGACY_OPTIONAL_COLUMNS.includes(key as string))
      ) {
        const hidden = LEGACY_OPTIONAL_COLUMNS.filter((key) => !visible.includes(key));
        storage.setItem(FINANCEIRO_OBRAS_TABLE_COLUMNS_KEY, JSON.stringify(hidden));
      } else {
        storage.removeItem(FINANCEIRO_OBRAS_TABLE_COLUMNS_KEY);
      }
    } catch {
      storage.removeItem(FINANCEIRO_OBRAS_TABLE_COLUMNS_KEY);
    }
  }
  storage.setItem(FINANCEIRO_TABLE_SETTINGS_FORMAT_KEY, SETTINGS_FORMAT_VERSION);
}

/**
 * Converte, na leitura, as preferências salvas antes do `useTableSettings` do DS: `confortavel` vira
 * `normal`, `compacto` vira `small` (mesma chave) e as colunas visíveis viram a lista de ocultas, para
 * ninguém perder a configuração. Roda antes do hook; sem armazenamento disponível não faz nada.
 */
export function migrateFinanceiroTableSettings(): void {
  try {
    migrateDensity(localStorage);
    migrateColumns(localStorage);
  } catch {
    // Sem armazenamento (modo privativo): o hook cai nos padrões.
  }
}
